// Global lead search — the header search box opens an overlay that queries /Leads/Search
// (CrmAgent5's SearchLeadsRich popup, rebuilt on the new shell). Results are rendered here so a
// keystroke never costs a page load; Enter/click opens the lead, Ctrl-click opens it in a new tab.

interface Hit {
  id: string;
  url: string;
  name: string;
  mobile: string;
  statusLabel: string;
  statusClass: string;
  destination?: string | null;
  pnr?: string | null;
  orderNumber?: number | null;
  publicist?: string | null;
  head?: string | null;
  agent?: string | null;
  pax?: string | null;
  archived: boolean;
  created: string;
  updated: string;
}

const esc = (s: unknown): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function initGlobalSearch(): void {
  // Inside the board's lead-popup iframe the shell is hidden — no search there.
  if (document.documentElement.classList.contains("embedded")) return;

  const modal = document.getElementById("globalSearch");
  const input = document.getElementById("globalSearchInput") as HTMLInputElement | null;
  const results = document.getElementById("globalSearchResults");
  const countEl = document.getElementById("globalSearchCount");
  const archiveEl = document.getElementById("globalSearchArchive") as HTMLInputElement | null;
  if (!modal || !input || !results) return;

  let hits: Hit[] = [];
  let active = -1;
  let timer = 0;
  let seq = 0; // guards against a slow earlier response overwriting a newer one

  const isOpen = () => modal.style.display !== "none";

  function open(seed = "") {
    modal!.style.display = modal!.dataset.display || "flex";
    if (seed) input!.value = seed;
    input!.focus();
    input!.select();
    if (input!.value.trim().length >= 2) run();
    else hint();
  }
  function close() {
    modal!.style.display = "none";
  }

  function hint() {
    hits = [];
    active = -1;
    if (countEl) countEl.textContent = "";
    results!.innerHTML =
      '<p class="py-10 text-center text-sm text-gray-400">הקלד שם, טלפון, ת.ז, מס׳ הזמנה, יעד או סוכן…</p>';
  }

  function chip(label: string, value: unknown): string {
    if (!value) return "";
    return `<span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600"><span class="text-gray-400">${esc(label)}</span>${esc(value)}</span>`;
  }

  function row(h: Hit, i: number): string {
    const initial = esc((h.name || "?").trim().charAt(0) || "?");
    const chips =
      chip("יעד", h.destination) +
      chip("הזמנה", h.orderNumber) +
      chip("PNR", h.pnr) +
      chip("יחצן", h.publicist) +
      chip("קודקוד", h.head) +
      chip("סוכן", h.agent) +
      chip("נוסע", h.pax) +
      (h.archived ? chip("", "ארכיון") : "");
    const phone = h.mobile
      ? `<a href="tel:${esc(h.mobile)}" dir="ltr" data-stop class="text-sm font-medium text-brand-600 hover:underline">${esc(h.mobile)}</a>`
      : "";
    return `<a href="${esc(h.url)}" data-hit="${i}"
      class="mb-2 flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 no-underline hover:border-brand-300 hover:shadow-sm">
      <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-500 text-base font-semibold text-white">${initial}</span>
      <span class="min-w-0 flex-1">
        <span class="flex flex-wrap items-center gap-2">
          <span class="font-semibold text-gray-900">${esc(h.name || "(ללא שם)")}</span>
          <span class="badge ${esc(h.statusClass)}">${esc(h.statusLabel)}</span>
          ${phone}
        </span>
        <span class="mt-1.5 flex flex-wrap gap-1.5">${chips}</span>
        <span class="mt-1.5 block text-[11px] text-gray-400">נוצר ${esc(h.created)} · עודכן ${esc(h.updated)}</span>
      </span>
    </a>`;
  }

  function paint() {
    results!.innerHTML = hits.map(row).join("");
    highlight();
  }

  function highlight() {
    results!.querySelectorAll<HTMLElement>("[data-hit]").forEach((el, i) => {
      const on = i === active;
      el.classList.toggle("border-brand-400", on);
      el.classList.toggle("bg-brand-25", on);
      if (on) el.scrollIntoView({ block: "nearest" });
    });
  }

  async function run() {
    const term = input!.value.trim();
    if (term.length < 2) { hint(); return; }
    const mine = ++seq;
    results!.innerHTML = '<p class="py-10 text-center text-sm text-gray-400">מחפש…</p>';
    try {
      const p = new URLSearchParams({ term, archive: String(!!archiveEl?.checked) });
      const res = await fetch(`/Leads/Search?${p.toString()}`, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      if (mine !== seq) return;
      const data = (await res.json()) as { results?: Hit[] };
      hits = data.results ?? [];
      active = hits.length ? 0 : -1;
      if (countEl) countEl.textContent = hits.length ? `${hits.length} תוצאות` : "";
      if (!hits.length) {
        results!.innerHTML = `<p class="py-10 text-center text-sm text-gray-400">לא נמצאו לידים עבור "${esc(term)}"</p>`;
        return;
      }
      paint();
    } catch {
      if (mine !== seq) return;
      results!.innerHTML = '<p class="py-10 text-center text-sm text-error-600">שגיאה בחיפוש</p>';
    }
  }

  // ---- wiring ----
  document.querySelectorAll<HTMLElement>("[data-global-search]").forEach((btn) =>
    btn.addEventListener("click", () => open()));
  document.querySelectorAll<HTMLElement>("[data-close-search]").forEach((btn) =>
    btn.addEventListener("click", close));
  modal.addEventListener("click", (e) => { if (e.target === modal) close(); });

  input.addEventListener("input", () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(run, 250);
  });
  archiveEl?.addEventListener("change", run);

  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!hits.length) return;
      e.preventDefault();
      active = (active + (e.key === "ArrowDown" ? 1 : hits.length - 1)) % hits.length;
      highlight();
    } else if (e.key === "Enter") {
      e.preventDefault();
      window.clearTimeout(timer);
      const hit = hits[active];
      if (!hit) { run(); return; }
      if (e.ctrlKey || e.metaKey) window.open(hit.url, "_blank");
      else location.href = hit.url;
    }
  });

  // Keep the phone link from hijacking the row navigation.
  results.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("[data-stop]")) e.stopPropagation();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen()) { close(); return; }
    // Ctrl/⌘+K anywhere, or "/" when not already typing somewhere.
    const typing = (e.target as HTMLElement | null)?.closest("input, textarea, select, [contenteditable]");
    if (((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") || (e.key === "/" && !typing && !isOpen())) {
      e.preventDefault();
      open();
    }
  });

  hint();
}
